import { userApis } from "@payping/api";
import { readAllMessages } from "@payping/redux/actions";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { submitQueryParams } from "@payping/utils/query-string";
import {
  Badge,
  Button,
  Drawer,
  LegacyCard,
  Pagination,
  Skeleton,
  Space,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import qs from "query-string";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsyncFn, useAsyncRetry } from "react-use";

import styles from "@/assets/scss/Messages.module.scss";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ListParams } from "@/utils/initialFilters";
import { useAppDispatch } from "@/utils/redux";
import { Close } from "@payping/icons";
import type { CardItemProps } from "@payping/web-components/legacyComponents/List/types";
import { useLastUrlWasPage } from "@/utils";
import { URLS } from "@payping/utilities-shared/consts";

interface MessageRowProps {
  date?: string;
  title?: string;
  description?: string;
  isSeen?: boolean;
  link?: string;
}

const MessageRow: React.FC<MessageRowProps> = ({ date, description, title, isSeen, link }) => {
  const data: CardItemProps["data"] = {
    descriptions: (
      <>
        <Typography.Text type={"secondary"}>{description}</Typography.Text>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer">
            {link}
          </a>
        ) : null}
      </>
    ),
    title: {
      primary: (
        <Typography.Title level={5} className={"tw:m-0! tw:font-medium"}>
          {title}
        </Typography.Title>
      ),
      tag: (
        <div className="tw:w-20 tw:text-end">
          <Typography.Text type={"secondary"}>
            {
              toJalali({
                date,
                dateFormat: dateFormats.standard.utcDateTime,
                toFormat: dateFormats.ui.jalaliDate,
              }).string
            }
          </Typography.Text>
        </div>
      ),
    },
  };

  return (
    <Badge dot={!isSeen} className={"tw:w-full"}>
      <div className={`${styles.rowContainer}`}>
        <LegacyCard data={data} />
      </div>
    </Badge>
  );
};

const Messages: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  const dispatch = useAppDispatch();
  const search: ListParams = qs.parse(location.search, { parseNumbers: true });
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const parsedSearch = {
    ...search,
    pageNumber: search.pageNumber || 1,
    pageSize: search.pageSize || 10,
  };
  const { pageSize, pageNumber } = parsedSearch;
  const { navigateToLast } = useLastUrlWasPage();
  const [drawerOpen, setDrawerOpen] = useState(true);

  const [, handleSeen] = useAsyncFn(() => dispatch(readAllMessages()), [dispatch]);

  const { loading, retry, value } = useAsyncRetry(async () => {
    const res = await userApis.messageWithOffsetWithLimitWithCancellationTokenGet(
      (pageNumber - 1) * pageSize,
      pageSize,
    );
    const haveUnseenMessage = res.messages?.some(({ isSeen }) => !isSeen);

    if (haveUnseenMessage) {
      handleSeen();
    }

    return res;
  }, [handleSeen, pageNumber, pageSize]);
  const messages = { count: value?.count, list: value?.messages };

  /* <RENDER CONTENT> */
  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.title}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      );
    }

    if (!messages.list) {
      return (
        <Typography.Title level={5} className={styles.title}>
          {t("message.noMessages")}
        </Typography.Title>
      );
    }

    return (
      <>
        {messages.list?.map(({ createdDateTime, description, isSeen, id, title, link }) => (
          <MessageRow
            date={createdDateTime}
            description={description}
            title={title}
            isSeen={isSeen}
            link={link}
            key={id}
          />
        ))}
        <Pagination
          className="tw:flex! tw:justify-center!"
          current={pageNumber}
          pageSize={pageSize}
          onChange={(page, newSize): void => {
            const newValues = {
              ...parsedSearch,
              pageNumber: page,
              pageSize: newSize,
            };
            submitQueryParams({
              navigate,
              newValues,
              parsedSearch,
              pathname: location.pathname,
              retry,
            });
          }}
          onShowSizeChange={(_currentPage, newSize): void => {
            const newValues = {
              ...parsedSearch,
              pageNumber: 1,
              pageSize: newSize,
            };
            submitQueryParams({
              navigate,
              newValues,
              parsedSearch,
              pathname: location.pathname,
              retry,
            });
          }}
          total={messages.count}
        />
      </>
    );
  };
  /* </RENDER CONTENT> */

  return (
    <Drawer
      open={drawerOpen}
      title={t("message.title")}
      size={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            setDrawerOpen(false);
            setTimeout(() => navigateToLast(), 300);
          }}
        />
      }
    >
      <div className={styles.containerMain}>
        <div className={styles.container}>
          {md ? (
            <Space size={"middle"} className={styles.containerTwo}>
              <img src={`${URLS.STORAGE_URL}/img/home/notif.png`} width={100} alt={"bell-notification"} />
            </Space>
          ) : null}
          {renderContent()}
        </div>
      </div>
    </Drawer>
  );
};

export default Messages;
